// Chickens and cows. They own a home building, wander near it, and produce
// something each morning if they were fed and are fond enough of you.
//
// The AI is deliberately small: pick a walkable tile within a short radius of
// home, amble to it, stand about for a moment, repeat. That is enough to make a
// yard feel occupied, and it costs one vector per animal per frame.
import { ANIMALS, ANIMAL_MAX_AFFECTION, type AnimalKind } from "./config.ts";
import type { Dir } from "./player.ts";
import { Prop, World } from "./world.ts";

type Pose = "idle" | "walk" | "peck";

export interface Animal {
  id: number;
  kind: AnimalKind;
  name: string;
  x: number;
  y: number;
  dir: Dir;
  /** where it wanders around */
  home: { x: number; y: number };
  affection: number;
  fed: boolean;
  /** already gave its produce today */
  produced: boolean;
  pose: Pose;
  target: { x: number; y: number } | null;
  timer: number;
  animTime: number;
  /** stops it being petted twice in a day */
  petted: boolean;
}

export interface AnimalSave {
  id: number;
  kind: AnimalKind;
  name: string;
  x: number;
  y: number;
  home: { x: number; y: number };
  affection: number;
  fed: boolean;
  produced: boolean;
  petted: boolean;
}

const CHICKEN_NAMES = ["Marge", "Nugget", "Pip", "Doris", "Cluck Norris", "Beak", "Hen Solo", "Bramble"];
const COW_NAMES = ["Buttercup", "Dot", "Moolissa", "Clover", "Bess", "Nutmeg"];

const WANDER_RADIUS = 5;

export class Animals {
  readonly list: Animal[] = [];
  private nextId = 1;
  private seed = 99;

  private rnd(): number {
    this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
    return this.seed / 0x7fffffff;
  }

  get count(): number {
    return this.list.length;
  }

  countOf(kind: AnimalKind): number {
    return this.list.filter((a) => a.kind === kind).length;
  }

  add(kind: AnimalKind, home: { x: number; y: number }): Animal {
    const names = kind === "chicken" ? CHICKEN_NAMES : COW_NAMES;
    const used = new Set(this.list.map((a) => a.name));
    const name = names.find((n) => !used.has(n)) ?? `${ANIMALS[kind].name} ${this.nextId}`;
    const a: Animal = {
      id: this.nextId++,
      kind,
      name,
      x: home.x + 0.5,
      y: home.y + 1.5,
      dir: "down",
      home: { ...home },
      affection: 0,
      fed: false,
      produced: false,
      pose: "idle",
      target: null,
      timer: 1,
      animTime: this.rnd() * 4,
      petted: false,
    };
    this.list.push(a);
    return a;
  }

  nearest(x: number, y: number, maxDist = 1.4): Animal | null {
    let best: Animal | null = null;
    let bestD = maxDist * maxDist;
    for (const a of this.list) {
      const d = (a.x - x) ** 2 + (a.y - y) ** 2;
      if (d < bestD) {
        bestD = d;
        best = a;
      }
    }
    return best;
  }

  update(dt: number, world: World, onCluck?: (a: Animal) => void): void {
    for (const a of this.list) {
      a.animTime += dt;
      a.timer -= dt;
      const def = ANIMALS[a.kind];

      if (a.target) {
        const dx = a.target.x - a.x;
        const dy = a.target.y - a.y;
        const len = Math.hypot(dx, dy);
        if (len < 0.08) {
          a.target = null;
          a.pose = this.rnd() > 0.5 ? "peck" : "idle";
          a.timer = 0.8 + this.rnd() * 2.4;
        } else {
          const step = Math.min(len, def.speed * dt);
          const nx = a.x + (dx / len) * step;
          const ny = a.y + (dy / len) * step;
          if (world.blocked(Math.floor(nx), Math.floor(ny))) {
            a.target = null;
            a.timer = 0.4;
          } else {
            a.x = nx;
            a.y = ny;
            a.dir = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? "right" : "left") : dy > 0 ? "down" : "up";
            a.pose = "walk";
          }
        }
        continue;
      }

      if (a.timer > 0) continue;

      // pick somewhere new near home
      for (let tries = 0; tries < 8; tries++) {
        const tx = Math.round(a.home.x + (this.rnd() - 0.5) * WANDER_RADIUS * 2);
        const ty = Math.round(a.home.y + 1 + (this.rnd() - 0.5) * WANDER_RADIUS * 2);
        if (!world.inBounds(tx, ty) || world.blocked(tx, ty)) continue;
        a.target = { x: tx + 0.5, y: ty + 0.5 };
        break;
      }
      a.timer = 1 + this.rnd() * 2;
      if (a.target === null) a.pose = "idle";
      if (onCluck && this.rnd() > 0.94) onCluck(a);
    }
  }

  /** Sprite for an animal this frame. */
  spriteOf(a: Animal): string {
    const def = ANIMALS[a.kind];
    if (a.pose === "walk") return `${def.prefix}_walk_${a.dir}_${Math.floor(a.animTime * 6) % 3}`;
    if (a.pose === "peck" && a.kind === "chicken") return `chicken_peck_${a.dir}_${Math.floor(a.animTime * 4) % 2}`;
    return `${def.prefix}_idle_${a.dir}_${Math.floor(a.animTime * 1.6) % 2}`;
  }

  /**
   * Overnight: fed animals gain affection and lay; hungry ones sulk and lose it.
   * Returns what to drop into the player's satchel.
   */
  nextDay(world: World): { key: string; count: number; names: string[] }[] {
    const yields = new Map<string, { count: number; names: string[] }>();
    const trough = world.findProp(Prop.Trough);
    const fedByTrough = trough !== null;
    for (const a of this.list) {
      const def = ANIMALS[a.kind];
      const fed = a.fed || fedByTrough;
      a.affection = Math.max(0, Math.min(ANIMAL_MAX_AFFECTION, a.affection + (fed ? 1 : -1) + (a.petted ? 1 : 0)));
      if (fed && a.affection >= def.minAffection) {
        const slot = yields.get(def.produce) ?? { count: 0, names: [] };
        slot.count++;
        slot.names.push(a.name);
        yields.set(def.produce, slot);
      }
      a.fed = false;
      a.petted = false;
      a.produced = false;
    }
    return [...yields].map(([key, v]) => ({ key, ...v }));
  }

  toJSON(): AnimalSave[] {
    return this.list.map((a) => ({
      id: a.id,
      kind: a.kind,
      name: a.name,
      x: a.x,
      y: a.y,
      home: a.home,
      affection: a.affection,
      fed: a.fed,
      produced: a.produced,
      petted: a.petted,
    }));
  }

  load(rows: AnimalSave[]): void {
    this.list.length = 0;
    this.nextId = 1;
    for (const r of rows) {
      const a: Animal = {
        id: r.id,
        kind: r.kind,
        name: r.name,
        x: r.x,
        y: r.y,
        dir: "down",
        home: r.home,
        affection: r.affection,
        fed: r.fed,
        produced: r.produced,
        pose: "idle",
        target: null,
        timer: 1,
        animTime: 0,
        petted: r.petted ?? false,
      };
      this.list.push(a);
      this.nextId = Math.max(this.nextId, r.id + 1);
    }
  }
}
